import Ajv from 'ajv';
import { isValidLatitude, isValidLongitude } from 'src/shared/helpers/coordinateHelpers';
import { habitatInsightsSamplesAjvValidationSchema, HabitatInsightsGeoJson, HabitatInsightsGeoJsonFile } from 'src/shared/types';
import { uniq } from 'lodash';
import { HabitatInsightsGeoJsonValidationError } from 'src/app/habitat-insights/state/habitatInsightsUploadSlice';

export const validateHabitatInsightsGeoJSON = (jsonArrayString: string): HabitatInsightsGeoJsonValidationError[] | [] => {
    try {
        const featureCollection: HabitatInsightsGeoJsonFile = JSON.parse(jsonArrayString);
        const errors: HabitatInsightsGeoJsonValidationError[] = [];

        try {
            const ajv = new Ajv();
            const validate = ajv.compile(habitatInsightsSamplesAjvValidationSchema);
            const isValid = validate(featureCollection);
            if (isValid) {
                if (uniq(featureCollection.features.map(entry => entry.properties.name)).length > 40) {
                    errors.push({
                        type: 'InvalidInputLength',
                        message:
                            '<p>More than 40 sites identified.</p> <p>Please ensure that 40 or fewer sites are listed in the file.</p>',
                    });
                }
                // Check for duplicate names
                const names = featureCollection.features
                    .map(entry => entry.properties.name)
                    .filter((name): name is string => name !== null);
                const uniqueNames = uniq(names);
                if (names.length !== uniqueNames.length) {
                    const duplicateNames = names.filter((name, index) => names.indexOf(name) !== index);
                    const uniqueDuplicates = uniq(duplicateNames);
                    errors.push({
                        type: 'DuplicateNameError',
                        message: `<p>Duplicate site names found.</p> <p>Please ensure that all site names are unique. Duplicate names: ${uniqueDuplicates.join(
                            ', '
                        )}</p>`,
                    });
                }
                featureCollection.features.forEach((geoJsonObject: HabitatInsightsGeoJson) => {
                    const coordinates = geoJsonObject.geometry.coordinates;
                    if (
                        !coordinates[0].every(
                            ([longitude, latitude]: [number, number]) => isValidLatitude(latitude) && isValidLongitude(longitude)
                        )
                    ) {
                        errors.push({
                            type: 'InvalidCoordinatesError',
                            message:
                                '<p>File contains invalid latitude(s) or longitude(s).</p><p> Please ensure latitude values range between -90 to 90 and longitude values range between -180 to 180.</p>',
                        });
                    }
                });
            } else {
                errors.push({
                    type: 'InvalidGeometryTypeError',
                    message:
                        '<p>File is not polygon geometry type.</p><p> Please ensure that the file only contains polygon geometry type.</p>',
                });
            }
        } catch (_error) {
            return [{ type: 'InvalidJSONFormatError', message: 'Invalid JSON format.' }];
        }
        return errors.length > 0 ? errors : [];
    } catch (_error) {
        return [{ type: 'InvalidJSONFormatError', message: 'Invalid JSON format.' }];
    }
};
